import { Button, Card, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import {
  useCreateProjectRestrictionMutation,
  useDeleteProjectRestrictionMutation,
  useGetProjectMembersQuery,
  useGetProjectQuery,
  useGetProjectRestrictionsQuery,
  useUpdateProjectAdminsMutation,
} from '../../../../../api/projects'
import type {
  IProjectTeamMemberResource,
  IProjectUserRestrictionResource,
  ProjectUserRestrictionLevel,
} from '../../../../../api/types/projects'
import { editProjectFieldSx, editProjectSelectFieldSx } from '../editProjectFieldSx'
import {
  ProjectRestrictionUserPicker,
  buildRestrictionLevelLabels,
  formatRestrictionUserLabel,
  formatRestrictionUserSlug,
  type SelectedRestrictionUser,
} from '../../../../../components/Moderation/RestrictionUserPicker'
import { extractApiErrorMessage } from '../../../../../utils/api'
import { InviteTeamToProjectCard } from './InviteTeamToProjectCard'
import { ProjectParticipatingTeamsCard } from './ProjectParticipatingTeamsCard'

const levelLabels = buildRestrictionLevelLabels('Скрыть проект')

export const EditAdministrationForm = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = Number(id)
  const { data: project } = useGetProjectQuery(projectId, { skip: !id || Number.isNaN(projectId) })
  const permissions = project?.viewer?.permissions

  const { data: members = [] } = useGetProjectMembersQuery(
    { projectId },
    { skip: !permissions?.can_manage_admins || !projectId },
  )
  const { data: restrictions = [], refetch: refetchRestrictions } = useGetProjectRestrictionsQuery(
    { projectId },
    { skip: !permissions?.can_moderate || !projectId },
  )

  const [updateAdmins, updateAdminsState] = useUpdateProjectAdminsMutation()
  const [createRestriction, createRestrictionState] = useCreateProjectRestrictionMutation()
  const [deleteRestriction] = useDeleteProjectRestrictionMutation()

  const editableMembers = useMemo(
    () => members.filter((member: IProjectTeamMemberResource) => !member.is_author),
    [members],
  )

  const [adminIds, setAdminIds] = useState<number[]>([])
  const [selectedUser, setSelectedUser] = useState<SelectedRestrictionUser | null>(null)
  const [restrictionLevel, setRestrictionLevel] = useState<ProjectUserRestrictionLevel>('comments')
  const [restrictionReason, setRestrictionReason] = useState('')
  const [restrictionSubmitError, setRestrictionSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const initial = editableMembers
      .filter((member: IProjectTeamMemberResource) => member.permission_role === 'admin')
      .map((member: IProjectTeamMemberResource) => member.user_id)
    setAdminIds(initial)
  }, [editableMembers])

  const toggleAdmin = (userId: number, checked: boolean) => {
    setAdminIds((prev) => {
      if (checked) {
        if (prev.includes(userId)) return prev
        if (prev.length >= 5) {
          toast.info('Не более 5 администраторов')
          return prev
        }
        return [...prev, userId]
      }
      return prev.filter((id) => id !== userId)
    })
  }

  const handleSaveAdmins = async () => {
    try {
      await updateAdmins({ projectId, body: { admin_user_ids: adminIds } }).unwrap()
      toast.success('Администраторы обновлены')
    } catch {
      toast.error('Не удалось сохранить администраторов')
    }
  }

  const handleAddRestriction = async () => {
    if (!selectedUser) {
      toast.info('Укажите имя профиля (@...) или выберите из подсказок')
      return
    }

    setRestrictionSubmitError(null)
    try {
      await createRestriction({
        projectId,
        body: {
          user_id: selectedUser.id,
          level: restrictionLevel,
          ...(restrictionReason.trim() ? { reason: restrictionReason.trim() } : {}),
        },
      }).unwrap()
      toast.success('Ограничение добавлено')
      setSelectedUser(null)
      setRestrictionReason('')
      void refetchRestrictions()
    } catch (error) {
      const message = extractApiErrorMessage(error) ?? 'Не удалось добавить ограничение'
      setRestrictionSubmitError(message)
      toast.error(message)
    }
  }

  const handleRemoveRestriction = async (item: IProjectUserRestrictionResource) => {
    try {
      await deleteRestriction({ projectId, userId: item.user_id }).unwrap()
      toast.success('Ограничение снято')
    } catch {
      toast.error('Не удалось снять ограничение')
    }
  }

  return (
    <Stack gap={2}>
      {permissions?.can_manage_admins && (
        <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
          <Stack gap={2}>
            <Typography variant="black-16-semiBold">Администраторы проекта</Typography>
            <Typography variant="black-14-light" color="text.secondary">
              Администраторы могут редактировать проект и модерировать комментарии — это права и ответственность.
              Не путать с «Соавтором» во вкладке «Команда» (только отображение). Не более 5 человек.
            </Typography>
            {editableMembers.length === 0 && (
              <Typography variant="black-14-light">Нет активных участников для назначения.</Typography>
            )}
            {editableMembers.map((member: IProjectTeamMemberResource) => {
              const userId = member.user_id
              const label = member.user.fullname || member.user.shortname || `ID ${userId}`
              return (
                <FormControlLabel
                  key={userId}
                  control={
                    <Switch
                      checked={adminIds.includes(userId)}
                      onChange={(e) => toggleAdmin(userId, e.target.checked)}
                    />
                  }
                  label={label}
                />
              )
            })}
            <Stack direction="row">
              <Button variant="follow" onClick={() => void handleSaveAdmins()} disabled={updateAdminsState.isLoading}>
                Сохранить администраторов
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {permissions?.can_moderate && (
        <>
          <ProjectParticipatingTeamsCard projectId={projectId} teams={project?.participating_teams ?? []} />
          <InviteTeamToProjectCard projectId={projectId} />
          <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
          <Stack gap={2}>
            <Typography variant="black-16-semiBold">Ограничения пользователей</Typography>
            <Typography variant="black-14-light" color="text.secondary">
              Укажите пользователя, уровень ограничения и нажмите «Добавить ограничение».
            </Typography>

            <ProjectRestrictionUserPicker
              projectId={projectId}
              selectedUser={selectedUser}
              onSelectedUserChange={(user) => {
                setSelectedUser(user)
                if (user) setRestrictionSubmitError(null)
              }}
              submitError={restrictionSubmitError}
            />

            <Stack gap={0.5}>
              <Typography variant="black-14-medium">Уровень ограничения</Typography>
              <TextField
                select
                size="small"
                value={restrictionLevel}
                onChange={(e) => setRestrictionLevel(e.target.value as ProjectUserRestrictionLevel)}
                sx={{ ...editProjectSelectFieldSx, width: { xs: '100%', sm: 280 } }}
                SelectProps={{
                  MenuProps: {
                    disableScrollLock: true,
                  },
                }}
                inputProps={{
                  'aria-label': 'Уровень ограничения',
                }}
              >
                <MenuItem value="full">{levelLabels.full}</MenuItem>
                <MenuItem value="comments">{levelLabels.comments}</MenuItem>
              </TextField>
            </Stack>

            <Stack gap={0.5}>
              <Typography variant="black-14-medium">Причина</Typography>
              <TextField
                placeholder="Необязательно"
                value={restrictionReason}
                onChange={(e) => setRestrictionReason(e.target.value.slice(0, 500))}
                size="small"
                fullWidth
                sx={editProjectFieldSx}
                inputProps={{
                  'aria-label': 'Причина ограничения',
                }}
              />
            </Stack>
            <Stack direction="row">
              <Button
                variant="follow"
                onClick={() => void handleAddRestriction()}
                disabled={createRestrictionState.isLoading || !selectedUser}
              >
                Добавить ограничение
              </Button>
            </Stack>

            {restrictions.length === 0 && (
              <Typography variant="black-14-light">Ограничений пока нет.</Typography>
            )}
            {restrictions.map((item: IProjectUserRestrictionResource) => {
              const name = formatRestrictionUserLabel(item.user)
              const slug = formatRestrictionUserSlug(item.user)
              return (
                <Stack
                  key={`${item.user_id}-${item.level}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={1}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack>
                    <Typography variant="black-14-normal">{name}</Typography>
                    <Typography variant="black-12-light" color="text.secondary">
                      {slug ? `${slug} · ` : ''}
                      {levelLabels[item.level]}
                      {item.reason ? ` · ${item.reason}` : ''}
                    </Typography>
                  </Stack>
                  <Button variant="danger" size="small" onClick={() => void handleRemoveRestriction(item)}>
                    Снять
                  </Button>
                </Stack>
              )
            })}
          </Stack>
        </Card>
        </>
      )}
    </Stack>
  )
}
